<template>
  <div
    v-hotkey="shortcuts"
    class="e-container"
    :class="plannerEventDetailsMainClassName"
  >
    <BasePaneHeader :has-nav="false" :is-hideable="false">
      <template #right>
        <div v-if="!creating && !isAllocation" class="recording-controls">
          <RecordingToolbar
            v-bind="recordingToolbarProps"
            @recording-control-click="onRecordingControlClick"
            @toggle-transcript="toggleTranscriptPopover"
            @close-transcript="closeRecordingTranscript"
          />
        </div>
        <EventDetailsMoreMenu
          v-if="isEditable && (!hasChanges || isDraftEvent)"
          ref="buttonMenuEl"
          :event="event"
          :task="task"
        />
      </template>
      <BaseCheckbox
        v-if="task"
        :is-checked="!!task.completed"
        :is-disabled="isBrokenIntegration"
        :checkbox-tooltip="
          taskCheckboxTooltip(!!task.completed, !!task.ignored)
        "
        style="margin-inline-end: 5px"
        @click="onCheckTask"
      />
      <BaseInput
        ref="titleEl"
        v-model:value="title"
        size="xxl"
        placeholder="Untitled"
        :is-edited="propsEdited.title"
        :is-disabled="!isEditable"
        :debounce="1000"
        @meta-enter="confirmChanges"
        @enter="onEnterInTitleInput"
      />
    </BasePaneHeader>
    <section
      class="event-details-section"
      :class="{ 'has-task-object': isAllocation }"
    >
      <DateAndTimePicker
        v-if="showEventTimeFields"
        v-model:event-time="time"
        :is-edited="propsEdited.time"
        :is-editable="isEditable && (isOrganizer || isCreator)"
        is-compact
        :is-different-day-pickable="true"
      />
      <BaseCheckbox
        v-if="isEditable && (isOrganizer || isCreator) && !isAllocation"
        class="all-day-checkbox"
        checkbox-size="sm"
        :is-checked="isAllDay"
        :is-checkbox-tooltip-shown="false"
        title="All day"
        @click="toggleAllDay"
      />
      <ButtonMenuRecurrence
        v-if="isEditable && (isOrganizer || isCreator) && !isAllocation"
        v-model:recurrence="recurrence"
        :time="time"
        :is-disabled="isEmpty(title)"
        @custom="$emit('openCustomRecurrence')"
      />
      <Recurrence
        v-else-if="!isAllocation"
        :value="recurrence"
        disabled
        compact
      />
      <Location
        v-if="!isAllocation"
        v-model:value="location"
        :edited="propsEdited.location"
        :disabled="!isEditable"
        compact
      />
      <BaseConferenceButton
        v-if="!creating && meeting && !isAllocation"
        :meeting-name="meeting.name"
        :is-rounded="false"
        is-meeting-name-shown
        :has-tooltip="{ withText: false, withShortcut: true }"
        @open-url="joinEvent"
      />
      <div v-if="showEventTimeFields" class="calendar-row">
        <CalendarTag
          v-model:value="calendarId"
          :editable="!event.recurrence && isEditable && isMoveAllowed"
          display="summary-next-to-tag"
          :has-padding="false"
          :has-tooltip="false"
        />
        <EventSourceIcon :calendar="calendar" />
      </div>
      <AttendanceControl
        v-if="!isAllocation"
        :disabled="!isEditable"
        :value="isActive"
        @update:value="setAttendance"
      />
      <Availability
        v-if="!isAllocation"
        :disabled="!isEditable"
        :value="isAvailable"
        @update:value="setAvailability"
      />
      <EventColorInfo :event="event" :task="task" />
      <div v-if="taskObject" class="divider">
        <Suspense>
          <ObjectComponent
            class="task-object-properties"
            :database-id="taskObject.databaseId"
            :workspace-id="taskObject.workspaceId"
            :id="taskObject.rowId"
            embedded-mode
          />
          <template #fallback>
            <BaseSkeletonList :is-compact="true" :has-icon="false" :size="4" />
          </template>
        </Suspense>
      </div>
      <ExtendedProperties
        v-else-if="isAllocation && task"
        class="extension-field-list divider"
        object-type="task"
        :object-id="task.id"
        :extension-object-id="task.extension_row"
        :create-extension-object-callback="ensureTaskExtensionObject"
      />
      <ExtendedProperties
        v-else-if="!isAllocation"
        class="extension-field-list divider"
        object-type="event"
        :object-id="event.id"
        :extension-object-id="event.extension_row"
        :create-extension-object-callback="ensureEventExtensionObject"
      />
    </section>
    <section
      v-if="!isAllocation && (isEditable || isMeeting)"
      class="divider attendeesContainer"
    >
      <AttendeesList
        v-if="calendarOwnerEmail && !isAllocation"
        v-model:attendees="attendees"
        class="attendees"
        :event="event"
        :calendar-owner-email="calendarOwnerEmail"
        :disabled="!event.editable"
        :is-openable="viewing"
        :is-attendable="event.attendable"
        :is-meeting-with-me="isMeetingWithMe"
        compact
        @create="confirmChanges"
      />
      <div
        v-if="(isEditable || !!description) && !isAllocation"
        class="description-wrapper divider"
        :class="{ hasChanges }"
      >
        <!-- Description -->
        <BaseInput
          v-if="isEditable && (!description || isEditingDescription)"
          ref="descriptionInput"
          v-model:value="description"
          placeholder="Add description…"
          :is-disabled="!isEditable"
          :is-edited="propsEdited.description"
          :is-one-line="false"
          class="description"
          has-styles
          @meta-enter="confirmChanges"
          @blur="isEditingDescription = false"
        />
        <div
          v-else-if="!!description"
          class="description description-html"
          :class="{ editable: isEditable }"
          v-html="sanitizedDescription"
          @click="onDescriptionClick"
        />
      </div>
    </section>
  </div>
</template>

<script lang="ts">
import { addMinutes, isSameDay, startOfDay } from "date-fns";
import { isEmpty } from "lodash-es";
import {
  computed,
  defineComponent,
  nextTick,
  onMounted,
  PropType,
  ref,
  toRefs,
} from "vue";

import type {
  Attendance,
  EventAvailability,
  FullTask,
} from "@ctrl/controller-rpc";

import { eventActions } from "@/components/actions/actions";
import BaseCheckbox from "@/components/base/BaseCheckbox.vue";
import BaseConferenceButton from "@/components/base/BaseConferenceButton.vue";
import BaseInput from "@/components/base/BaseInput.vue";
import BasePaneHeader from "@/components/base/BasePaneHeader.vue";
import BaseSkeletonList from "@/components/base/BaseSkeletonList.vue";
import ButtonMenuRecurrence from "@/components/ButtonMenuRecurrence.vue";
import DateAndTimePicker from "@/components/date-time/DateAndTimePicker.vue";
import AttendanceControl from "@/components/elements/Attendance.vue";
import AttendeesList from "@/components/elements/AttendeesList.vue";
import Availability from "@/components/elements/Availability.vue";
import CalendarTag from "@/components/elements/CalendarTag.vue";
import EventSourceIcon from "@/components/elements/EventSourceIcon.vue";
import Location from "@/components/elements/Location.vue";
import { useEventInfo } from "@/components/eventInfo";
import ExtendedProperties from "@/components/ExtendedProperties.vue";
import ObjectComponent from "@/components/graph/Object.vue";
import * as inject from "@/components/inject";
import { getEventShortcuts } from "@/components/object-shortcuts";
import {
  FullEvent,
  PropsEdited,
  useEventDetails,
} from "@/components/planner/controllers/event";
import EventColorInfo from "@/components/planner/sidebar/EventColorInfo.vue";
import EventDetailsMoreMenu from "@/components/planner/sidebar/EventDetailsMoreMenu.vue";
import { EventDetailsMode } from "@/components/planner/types";
import RecordingToolbar from "@/components/RecordingToolbar.vue";
import Recurrence from "@/components/Recurrence.vue";
import {
  assignShortcuts,
  hideDetails,
  Shortcuts,
  validateAndOpen,
} from "@/components/shortcuts";
import { taskCheckboxTooltip } from "@/components/task/checkbox-tooltip";
import { useAudioRecording } from "@/composables/recording/useAudioRecording";
import {
  STORAGE_KEY,
  useAutoMeetingRecording,
} from "@/composables/recording/useAutoMeetingRecording";
import { useMeetingRecording } from "@/composables/recording/useMeetingRecording";
import { useRecordingToolbar } from "@/composables/recording/useRecordingToolbar";
import { useBrowserStore } from "@/composables/useBrowserStore";
import { useExtendedProperties } from "@/composables/useExtendedProperties";
import type { MeetingRecordingContext } from "@/recording/types";
import { bindAsync, releasableRef, useReleaser } from "@/ref";
import { completeRestoreTask } from "@/task";
import { html } from "@/utils/html";
import { isBoundTaskId } from "@/utils/id";
import { openExternalUrl } from "@/utils/open-external-url";

export const plannerEventDetailsMainClassName = "planner-event-details";

export default defineComponent({
  components: {
    EventDetailsMoreMenu,
    AttendeesList,
    AttendanceControl,
    BaseCheckbox,
    BaseConferenceButton,
    BaseInput,
    BasePaneHeader,
    BaseSkeletonList,
    ButtonMenuRecurrence,
    CalendarTag,
    DateAndTimePicker,
    EventColorInfo,
    EventSourceIcon,
    ExtendedProperties,
    ObjectComponent,
    Availability,
    Location,
    Recurrence,
    RecordingToolbar,
  },
  props: {
    event: { type: Object as PropType<FullEvent>, required: true },
    hasChanges: { type: Boolean, required: true },
    isDraftEvent: { type: Boolean, required: true },
    isEditable: { type: Boolean, required: true },
    mode: {
      type: String as PropType<EventDetailsMode>,
      default: EventDetailsMode.view,
    },
    propsEdited: { type: Object as PropType<PropsEdited>, required: true },
    task: { type: Object as PropType<FullTask>, required: false },
  },
  emits: {
    "update:event": (_: Event) => true,
    confirmChanges: () => true,
    killEventDetails: () => true,
    openCustomRecurrence: () => true,
    updatePlannerView: (_: Date) => true,
  },
  async setup(props, { emit }) {
    const { event, mode, task, hasChanges, isDraftEvent, isEditable } =
      toRefs(props);
    const contextMenu = inject.contextMenu();
    const clock = inject.clock();
    const controller = inject.controller();
    const profile = inject.profile();
    const calendars = inject.calendars();
    const logger = inject.logger("EventDetails");
    const baseLogger = logger.base();
    const billing = inject.billing();
    const isInputFocused = inject.isInputFocused();
    const isModalShown = inject.isModalShown();
    const router = inject.router();
    const multipleEventsModal = inject.multipleEventsModal();
    const simpleModal = inject.simpleModal();
    const menu = inject.menu();
    const palette = inject.palette();
    const releaser = useReleaser();
    type TaskObjectIds = {
      workspaceId: string;
      databaseId: string;
      rowId: string;
    };
    const taskObjectPromise = releaser.add(
      bindAsync(task, async (current) => {
        let ids: TaskObjectIds | null = null;
        if (current && isBoundTaskId(current.id)) {
          try {
            const parsed = await controller.graph.object.id.parse(current.id);
            if (parsed.workspace_id && parsed.table_id && parsed.row_id) {
              ids = {
                workspaceId: parsed.workspace_id,
                databaseId: parsed.table_id,
                rowId: parsed.row_id,
              };
            }
          } catch {
            ids = null;
          }
        }
        return releasableRef(ids);
      })
    );
    const titleEl = ref<InstanceType<typeof BaseInput>>();
    const buttonMenuEl = ref<InstanceType<typeof EventDetailsMoreMenu>>();
    const focusTitleInput = () => titleEl.value?.focus();
    onMounted(() => {
      if (creating.value && !isModalShown.value) nextTick(focusTitleInput);
    });
    const audioRecording = releaser.add(useAudioRecording(baseLogger));
    const meetingRecordingContext = computed<MeetingRecordingContext>(() => ({
      id: event.value.id,
      title: event.value.title ?? "",
      target: {
        kind: "event",
        start: event.value.start,
        end: event.value.end,
        active: event.value.active,
        attendable: event.value.attendable,
        link: event.value.link,
      },
    }));
    const meetingRecording = useMeetingRecording(
      baseLogger,
      billing,
      audioRecording,
      meetingRecordingContext
    );
    // Namespaced per account (adopting the value written un-namespaced by
    // older versions on first read).
    const { migrate, syncState } = useBrowserStore(
      profile.value.user.accountId
    );
    migrate(STORAGE_KEY);
    const autoMeetingRecording = releaser.add(
      syncState<boolean>(STORAGE_KEY, true)
    );
    const {
      attendees,
      calendar: calendarId,
      description,
      location,
      recurrence,
      time,
      title,
    } = useEventDetails(event, controller, (date) =>
      emit("updatePlannerView", date)
    );
    const creating = computed(() => mode.value === EventDetailsMode.create);
    const viewing = computed(() => mode.value === EventDetailsMode.view);

    const eventInfo = useEventInfo(controller, calendars, event);
    releaser.add(
      eventInfo.then((info) => ({ release: () => info.releaseEventInfo() }))
    );
    const [
      {
        calendar,
        calendarOwnerEmail,
        isMeeting,
        isMeetingWithMe,
        isMoveAllowed,
        isOrganizer,
        isCreator,
        meeting,
        isWritable,
      },
      taskObject,
    ] = await Promise.all([eventInfo, taskObjectPromise]);
    const ensureEventExtensionObject = useExtendedProperties(
      controller,
      profile,
      "event",
      () => event.value?.extension_row,
      (id: string) => {
        if (event.value) {
          event.value.extension_row = id;
        }
      },
      async (id: string) => {
        if (event.value) {
          await controller.event.update(
            { id: event.value.id, extension_row: id },
            false,
            "app"
          );
        }
      }
    );
    const ensureTaskExtensionObject = useExtendedProperties(
      controller,
      profile,
      "task",
      () => task.value?.extension_row,
      (id: string) => {
        if (task.value) {
          task.value.extension_row = id;
        }
      },
      async (id: string) => {
        if (task.value) {
          await controller.task.update(
            task.value.id,
            { extension_row: id },
            "app",
            null
          );
        }
      }
    );

    const joinEvent = () =>
      eventActions.openConference.action(
        { type: "event", value: event.value },
        { controller }
      );
    const {
      release: releaseAutoMeetingRecording,
      errorMessage: autoMeetingRecordingError,
      willAutoRecordEvent,
      recordingStartsInLabel,
      markAutoMeetingRecordinngStarted: markAutoAIMeetingRecordingStarted,
      markAutoMeetingRecordinngStopped: markAutoAIMeetingRecordingStopped,
    } = useAutoMeetingRecording(
      baseLogger,
      clock,
      meetingRecording,
      meetingRecordingContext,
      autoMeetingRecording,
      true
    );
    releaser.defer(releaseAutoMeetingRecording);
    const mergedRecordingError = computed(
      () =>
        meetingRecording.recordingError.value || autoMeetingRecordingError.value
    );
    const recordingAutoStartLabel = computed(() => {
      if (!willAutoRecordEvent.value || !recordingStartsInLabel.value)
        return null;
      return recordingStartsInLabel.value;
    });
    const getAutoMeetingRecordingError = () => autoMeetingRecordingError.value;
    const clearAutoMeetingRecordingError = () => {
      autoMeetingRecordingError.value = null;
    };

    const {
      release: releaseRecordingToolbar,
      recordingToolbarProps,
      onRecordingControlClick,
      toggleTranscriptPopover,
      closeRecordingTranscript,
    } = useRecordingToolbar(meetingRecording, audioRecording, {
      getExtraRecordingError: getAutoMeetingRecordingError,
      onAfterStartRecording: markAutoAIMeetingRecordingStarted,
      onAfterStopRecording: markAutoAIMeetingRecordingStopped,
      clearErrorsOnToggle: clearAutoMeetingRecordingError,
      toolbar: {
        compact: true,
        tint: "red",
        transcriptAnchor: "left",
        recordLabel: "Record meeting",
        getAutoStartLabel: () => recordingAutoStartLabel.value,
      },
    });
    releaser.defer(releaseRecordingToolbar);

    const {
      partialText,
      status,
      isRecordingHere: isCurrentEventRecording,
      recordButtonState,
      showTranscriptToggle,
      transcriptEntries,
    } = meetingRecording;

    const confirmChanges = () => emit("confirmChanges");
    const isEscapeAllowed = computed<boolean>(
      () =>
        !(
          simpleModal.value.isDisplayed ||
          multipleEventsModal.value.isDisplayed ||
          contextMenu.value.isDisplayed
        )
    );
    const shortcuts = computed<Shortcuts>(() => {
      const injection = { isModalShown, isInputFocused };
      const genericShortcuts = {
        ...assignShortcuts(
          injection,
          hideDetails,
          () => {
            logger.info("Hiding event details from the Escape shortcut", {
              eventId: event.value?.id,
            });
            emit("killEventDetails");
          },
          ref(isEscapeAllowed.value && !buttonMenuEl.value?.isShown)
        ),
        ...assignShortcuts(
          injection,
          ref([validateAndOpen.value, "enter"]),
          confirmChanges,
          ref(hasChanges.value || isDraftEvent.value)
        ),
      };
      return {
        ...genericShortcuts,
        ...getEventShortcuts(
          { event, task, isWritable, calendar },
          {
            ...injection,
            controller,
            logger: baseLogger,
            profile,
            router,
            simpleModal,
            multipleEventsModal,
            menu,
            palette,
          },
          {
            closeContextMenu: contextMenu.value.hide,
            preventOpen: hasChanges,
          }
        ),
      };
    });
    const onCheckTask = () => {
      if (task.value) void completeRestoreTask(controller, task.value, "app");
    };
    const onEnterInTitleInput = () => {
      if (isDraftEvent.value) confirmChanges();
    };
    const isAllocation = computed<boolean>(() => !!task.value);
    // Bound tasks already surface this allocation via Object.vue's Allocations
    // property; keep the event time/calendar editors for drafts (the new slot
    // is not in Allocations yet) and for native tasks (no graph object page).
    const showEventTimeFields = computed(
      () => !taskObject.value || isDraftEvent.value
    );
    const isBrokenIntegration = computed(
      () => task.value?.integrationStatus?.status === "broken"
    );
    const isActive = computed<Attendance>(() => {
      if (event.value.active != null) {
        return event.value.active ? "attend" : "ignore";
      }
      if (event.value.attendance) return event.value.attendance;
      const isPrimaryCalendar = ["default", "primary"].includes(
        calendar.value?.role || ""
      );
      return isPrimaryCalendar ? "attend" : "ignore";
    });
    const setAttendance = (value: Attendance) => {
      event.value.attendance = value;
    };
    const isAvailable = computed<EventAvailability>(() => {
      return event.value.available;
    });
    const setAvailability = (value: EventAvailability) => {
      event.value.availability_override = value;
    };
    const sanitizedDescription = computed(() =>
      html.sanitize(description.value)
    );

    const isEditingDescription = ref(false);
    const descriptionInput = ref<InstanceType<typeof BaseInput> | null>(null);

    const closestDescriptionLink = (e: MouseEvent) => {
      const target =
        e.target instanceof Element
          ? e.target
          : e.target instanceof Node
            ? e.target.parentElement
            : null;
      return target?.closest("a") ?? null;
    };

    const handleDescriptionClick = (e: MouseEvent) => {
      const link = closestDescriptionLink(e);
      if (link) {
        e.preventDefault();
        const href = link.getAttribute("href");
        if (href) openExternalUrl(href, controller);
      }
    };

    const onDescriptionClick = (e: MouseEvent) => {
      handleDescriptionClick(e);
      if (!isEditable.value) return;
      if (closestDescriptionLink(e)) return; // already handled link
      isEditingDescription.value = true;
      nextTick(() => {
        const input = descriptionInput.value;
        const inputEl = input ? Reflect.get(input, "inputEl") : undefined;
        if (inputEl instanceof HTMLElement) {
          inputEl.style.minHeight = "100px";
        }
        input?.focus?.();
      });
    };

    const isAllDay = computed(() => !("start_time" in time.value));

    const toggleAllDay = () => {
      if (isAllDay.value) {
        // Convert to timed event
        if ("date" in time.value) {
          const start_time = new Date(time.value.date);
          start_time.setHours(10, 0, 0, 0);
          time.value = {
            start_time,
            end_time: addMinutes(start_time, 30),
          };
        } else if ("start_date" in time.value && "end_date" in time.value) {
          const start_time = new Date(time.value.start_date);
          start_time.setHours(10, 0, 0, 0);
          time.value = {
            start_time,
            end_time: addMinutes(start_time, 30),
          };
        }
      } else {
        // Convert to all day
        if ("start_time" in time.value && "end_time" in time.value) {
          if (isSameDay(time.value.start_time, time.value.end_time)) {
            time.value = { date: startOfDay(time.value.start_time) };
          } else {
            time.value = {
              start_date: startOfDay(time.value.start_time),
              end_date: startOfDay(time.value.end_time),
            };
          }
        }
      }
    };

    return {
      isActive,
      attendees,
      buttonMenuEl,
      calendar,
      calendarId,
      calendarOwnerEmail,
      confirmChanges,
      creating,
      description,
      ensureEventExtensionObject,
      ensureTaskExtensionObject,
      descriptionInput,
      handleDescriptionClick,
      isAllDay,
      isAllocation,
      isAvailable,
      isBrokenIntegration,
      isCreator,
      isCurrentEventRecording,
      isEmpty,
      isEditingDescription,
      isMeeting,
      isMeetingWithMe,
      isMoveAllowed,
      isOrganizer,
      joinEvent,
      location,
      meeting,
      recordingToolbarProps,
      onRecordingControlClick,
      toggleTranscriptPopover,
      closeRecordingTranscript,
      onCheckTask,
      onDescriptionClick,
      onEnterInTitleInput,
      partialText,
      setAttendance,
      setAvailability,
      plannerEventDetailsMainClassName,
      recurrence,
      recordingError: mergedRecordingError,
      recordingStartsInLabel,
      recordButtonState,
      willAutoRecordEvent,
      showEventTimeFields,
      showTranscriptToggle,
      status,
      shortcuts,
      sanitizedDescription,
      taskCheckboxTooltip,
      taskObject,
      time,
      title,
      titleEl,
      transcriptEntries,
      toggleAllDay,
      viewing,
    };
  },
});
</script>

<style lang="scss" scoped>
@use "@/styles/_variables.scss" as *;

.e-container {
  display: flex;
  flex-direction: column;
  width: 100%;
  max-height: 100vh;

  .event-details-section > div {
    min-height: 29px;
  }

  .event-details-section.has-task-object {
    overflow-y: auto;
    flex: 1;
    min-height: 0;
    padding-bottom: 84px;
  }

  .task-object-properties {
    width: 100%;

    :deep(.object) {
      width: 100%;
      overflow: visible;
    }

    :deep(.object .object-content) {
      margin: 0;
      width: 100%;
      max-width: none;
      align-items: flex-start;
      row-gap: 0;
    }

    :deep(.object .object-content .centered-content) {
      max-width: none;
    }

    :deep(.object .object-content .props) {
      row-gap: 0;
    }

    :deep(.graph-record.full > .option-property > .props) {
      row-gap: 0;
    }
  }

  :deep(.extended-properties .graph-record.full > .option-property > .props) {
    row-gap: 0;
  }

  .calendar-row {
    display: flex;
    flex-direction: row;
    align-items: center;
    column-gap: 8px;
  }

  .all-day-checkbox {
    padding-block: 0;
    padding-inline-end: 10px;
    padding-inline-start: 9px;

    :deep(input[type="checkbox"]) {
      margin-inline-end: 8px;
    }
  }

  .attendeesContainer {
    overflow-y: scroll;
    height: 100%;
    padding-bottom: 48px;
    padding: 0;
  }

  section {
    display: flex;
    flex-direction: column;
    row-gap: 4px;
    padding: 8px;

    :deep(.all-day-checkbox) {
      label span {
        @include themed() {
          color: t($grey-70);
        }
      }
    }

    :deep(.location .field .icon-container .icon path.fill),
    :deep(
      button.base-button.none.auxiliary.disabled
        .left
        :not(.trueColorsIcon)
        svg
        .fill
    ),
    :deep(
      button.base-button.none.auxiliary.default
        .left
        :not(.trueColorsIcon)
        svg
        .fill
    ) {
      @include themed() {
        fill: t($grey-40);
      }
    }

    .description-wrapper {
      :deep(.textarea) {
        @extend %font-small-regular;
        &:not(.isEdited) {
          @include themed() {
            color: t($grey-50);
          }
        }
      }
    }

    .recording-controls {
      position: relative;
      display: flex;
      flex-direction: column;
      row-gap: 4px;
    }

    .description-html {
      @extend %font-small-regular;
      word-break: break-word;

      @include themed() {
        color: t($grey-50);
      }

      :deep(a) {
        text-decoration: underline;
        cursor: pointer;
        @include transition(color 100ms ease-out);
        @include themed() {
          color: t($grey-40);
        }

        &:hover {
          @include themed() {
            color: t($grey-70);
          }
        }
      }
    }
  }

  .extension-field-list.extended-properties {
    margin-top: 0;
    width: 100%;
  }

  :deep(.extended-properties .object .object-content) {
    row-gap: 0px;
  }

  :deep(
    .extended-properties .option-property.full .property-display .property
  ) {
    height: 25px;
    width: auto;
    margin-inline-end: 8px;
    padding-inline-start: 3px;
    column-gap: 11px;
  }

  :deep(
    .extended-properties .option-property.full .property-display .property-label
  ) {
    padding-inline-start: 4px;
    column-gap: 10px;
  }

  :deep(
    .extended-properties
      .option-property.full
      .property-display
      .property-label
      .name
  ) {
    @extend %font-caption-regular;
    @include themed() {
      color: t($grey-70);
    }
  }

  :deep(.extended-properties .option-property .property-value span.empty) {
    @extend %font-caption-regular;
    padding-inline-start: 6px;
  }

  :deep(.extension-field-list .base-type.inline.instance) {
    padding-block: 1px;
    padding-inline-end: 6px;
    padding-inline-start: 2px;
    line-height: 19px;

    span {
      @extend %font-caption-regular;
    }

    .image {
      padding: 4px;

      .icon {
        font-size: 8px;
        line-height: 8px;

        &,
        svg {
          height: 8px;
          width: 8px;
        }
      }
    }
  }

  :deep(.extension-field-list .base-type.inline.instance:not(:hover) span) {
    @include themed() {
      color: t($grey-60);
    }
  }

  .divider {
    @include themed() {
      border-top: 1px solid t($grey-15);
    }
  }

  .event-details-section > .divider {
    margin-top: 8px;
    padding-top: 8px;
  }

  .attendees,
  .description-wrapper {
    padding: 8px;
  }

  .description-wrapper {
    flex-shrink: 1;
    padding-bottom: 84px;
  }
}
</style>
